/**
 * Bloqueio de página por módulo do plano
 *
 * Quando o plano do estabelecimento não inclui o módulo, mostra um convite
 * para upgrade em vez de renderizar a página (evita erros 403 em cascata).
 *
 * @author Equipe VendeAi
 * @since 1.3.0
 */

import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Lock, Sparkles } from 'lucide-react';
import { useAuth } from '@hooks/useAuth';
import { PLAN_MODULE_LABELS, PlanModuleKey, usePlanAccess } from '@hooks/usePlanAccess';

export function UpgradeNotice({
  module,
  compact = false,
}: {
  module: PlanModuleKey;
  compact?: boolean;
}) {
  const { user } = useAuth();
  const { summary } = usePlanAccess();
  const canUpgrade = user?.role === 'OWNER' || user?.role === 'MANAGER';

  return (
    <div
      className={`mx-auto flex max-w-xl flex-col items-center rounded-2xl border border-gray-200 bg-white text-center dark:border-gray-700 dark:bg-gray-800 ${
        compact ? 'p-6' : 'mt-10 p-10'
      }`}
      role="status"
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary-100 text-secondary-800 dark:bg-secondary-900/40 dark:text-secondary-300">
        <Lock size={26} />
      </span>
      <h2 className="mt-5 text-xl font-bold text-gray-900 dark:text-white">
        {PLAN_MODULE_LABELS[module]} não está no seu plano
      </h2>
      <p className="mt-2 text-gray-600 dark:text-gray-400">
        {summary?.plan
          ? `O plano ${summary.plan.name} não inclui este recurso.`
          : 'Seu plano não inclui este recurso.'}{' '}
        Faça upgrade para liberar agora, sem perder nenhum dado.
      </p>
      {canUpgrade ? (
        <Link to="/assinatura" className="btn btn-primary mt-6">
          <Sparkles size={16} /> Ver planos e fazer upgrade
        </Link>
      ) : (
        <p className="mt-6 text-sm text-gray-500">
          Peça ao responsável pelo estabelecimento para fazer o upgrade.
        </p>
      )}
    </div>
  );
}

export function PlanGate({ module, children }: { module: PlanModuleKey; children: ReactNode }) {
  const { hasModule, loading } = usePlanAccess();
  // Aguarda o plano para não disparar requisições bloqueadas pela página
  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={28} className="animate-spin text-secondary-700" aria-label="Carregando" />
      </div>
    );
  }
  if (!hasModule(module)) return <UpgradeNotice module={module} />;
  return <>{children}</>;
}
